import { currencySign } from '@utils/helpers';
import React, { useEffect, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import { useIntl } from 'react-intl';
import { fetchTransactions } from '../../../api/taptotip/transaction';
import messages from './messages';

const ranges = Object.freeze({
  sevenDays: 7,
  thirtyDays: 30
});

// Mirrors the amount fallback chain used by the TippingPayments table so the
// chart matches what the guide sees in their payments list.
const transactionAmount = transaction =>
  transaction.paymentDetails?.tripadmitValues?.amountOfTransactionGuideAskedFor ||
  transaction.paymentValue ||
  transaction.amount ||
  0;

const isChartableTransaction = transaction =>
  transaction.status === 'success' && ['tip', 'commission'].includes(transaction.type || 'tip');

const startOfDay = date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

function bucketByDay(transactions, days) {
  const today = startOfDay(new Date());
  const buckets = [];

  for (let i = days - 1; i >= 0; i--) {
    const day = new Date(today);
    day.setDate(today.getDate() - i);
    buckets.push({ date: day, total: 0 });
  }

  const firstDay = buckets[0].date.getTime();
  transactions.forEach(transaction => {
    const day = startOfDay(new Date(transaction.date)).getTime();
    if (day < firstDay) return;
    const bucket = buckets.find(b => b.date.getTime() === day);
    if (bucket) bucket.total += transactionAmount(transaction);
  });

  return buckets;
}

const EarningsTrendChart = ({ user }) => {
  const intl = useIntl();
  const [transactions, setTransactions] = useState([]);
  const [days, setDays] = useState(ranges.sevenDays);

  useEffect(() => {
    fetchTransactions({})
      .then(data => setTransactions((data || []).filter(isChartableTransaction)))
      .catch(err => console.error('Failed to load transactions for earnings chart:', err));
  }, []);

  const sign = currencySign(user.profile.currency);

  const { series, options, hasData } = useMemo(() => {
    const buckets = bucketByDay(transactions, days);
    const today = startOfDay(new Date()).getTime();

    const labels = buckets.map(b =>
      days === ranges.sevenDays
        ? b.date.getTime() === today
          ? intl.formatMessage(messages.today)
          : b.date.toLocaleDateString(intl.locale, { weekday: 'short' })
        : b.date.toLocaleDateString(intl.locale, { day: 'numeric', month: 'short' })
    );

    return {
      hasData: buckets.some(b => b.total > 0),
      series: [
        {
          name: intl.formatMessage(messages.title),
          data: buckets.map(b => Number((b.total / 100).toFixed(2)))
        }
      ],
      options: {
        chart: {
          type: 'area',
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'inherit'
        },
        colors: ['#6366f1'],
        stroke: { curve: 'smooth', width: 3, lineCap: 'round' },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0, stops: [0, 100] }
        },
        dataLabels: { enabled: false },
        markers: { size: days === ranges.sevenDays ? 4 : 0, strokeWidth: 2, strokeColors: '#fff' },
        grid: { borderColor: 'rgba(148, 163, 184, 0.15)', strokeDashArray: 4 },
        xaxis: {
          categories: labels,
          tickAmount: days === ranges.sevenDays ? undefined : 6,
          labels: { style: { colors: '#94a3b8', fontSize: '11px', fontWeight: 600 } },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false }
        },
        yaxis: {
          labels: {
            style: { colors: '#94a3b8', fontSize: '11px' },
            formatter: value => `${sign}${value.toFixed(0)}`
          },
          min: 0,
          forceNiceScale: true
        },
        tooltip: { y: { formatter: value => `${sign}${value.toFixed(2)}` } }
      }
    };
  }, [transactions, days, intl.locale]);

  return (
    <div className="w-full bg-white rounded-3xl shadow-sm border border-solid border-slate-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-slate-900 font-bold">{intl.formatMessage(messages.title)}</div>
          <div className="text-xs text-slate-400">{intl.formatMessage(messages.subtitle)}</div>
        </div>
        <div className="flex gap-1.5">
          {[
            { value: ranges.sevenDays, label: intl.formatMessage(messages.sevenDays) },
            { value: ranges.thirtyDays, label: intl.formatMessage(messages.thirtyDays) }
          ].map(range => (
            <button
              key={range.value}
              type="button"
              onClick={() => setDays(range.value)}
              className={`px-2 py-1 text-[10px] font-semibold rounded-md border-0 cursor-pointer transition-colors ${
                days === range.value
                  ? 'bg-indigo-50 text-indigo-600'
                  : 'bg-transparent text-slate-400 hover:bg-slate-100'
              }`}>
              {range.label}
            </button>
          ))}
        </div>
      </div>
      <Chart series={series} type="area" options={options} height="220px" />
      {!hasData && (
        <div className="text-center text-xs text-slate-400 -mt-2">
          {intl.formatMessage(messages.noData)}
        </div>
      )}
    </div>
  );
};

export default EarningsTrendChart;
